import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Outlet, redirect, useRouterState } from "@tanstack/react-router";
import { Skeleton } from "@vd/ui";
import { useState } from "react";
import { billingSummaryQuery, statusLine } from "../../../api/billingPlan";
import { planQuery } from "../../../api/plans";
import { requireAdmin } from "../../../auth/useMe";
import { BillingHeaderSlot } from "../../../components/billing/HeaderSlot";
import { Crumbs } from "../../../components/Crumbs";
import { Tabs } from "../../../components/ui";
import { useNavigate } from "../../../lib/navigate";
import { periodLine } from "../../../lib/seats";

// Admin > Company > Billing (DESIGN.md "Billing (console)"): one header and line tabs, each tab
// at its own address so Back and links land on it. With billing off (the summary's 404) only
// Plan and Usage show, and the other tabs' addresses open Plan.

export type BillingTab =
  | "/admin/billing"
  | "/admin/billing/invoices"
  | "/admin/billing/payment-methods"
  | "/admin/billing/details"
  | "/admin/billing/usage";

const TABS: { value: BillingTab; label: string; needsBilling: boolean }[] = [
  { value: "/admin/billing", label: "Plan", needsBilling: false },
  { value: "/admin/billing/invoices", label: "Invoices", needsBilling: true },
  { value: "/admin/billing/payment-methods", label: "Payment methods", needsBilling: true },
  { value: "/admin/billing/details", label: "Billing details", needsBilling: true },
  { value: "/admin/billing/usage", label: "Usage", needsBilling: false },
];

const BILLING_ONLY = new Set<string>(TABS.filter((t) => t.needsBilling).map((t) => t.value));

export const Route = createFileRoute("/admin/billing")({
  staticData: { crumb: { label: "Billing", parents: [] } },
  beforeLoad: async ({ context, location }) => {
    requireAdmin({ context });
    const path = location.pathname.replace(/\/+$/, "");
    if (!BILLING_ONLY.has(path)) return;
    const summary = await context.queryClient.ensureQueryData(billingSummaryQuery).catch(() => undefined);
    if (summary === null) throw redirect({ to: "/admin/billing", replace: true });
  },
  // The tabs depend on billing being on: known before the first frame, so they never shift.
  loader: ({ context }) => context.queryClient.ensureQueryData(billingSummaryQuery).catch(() => undefined),
  component: BillingLayout,
});

/** The tab an address belongs to: the longest tab address it starts with. */
export function tabOf(pathname: string): BillingTab {
  const path = pathname.replace(/\/+$/, "");
  return (
    TABS.slice(1).find((t) => path === t.value || path.startsWith(`${t.value}/`))?.value ?? "/admin/billing"
  );
}

function BillingLayout() {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const summary = useQuery(billingSummaryQuery);
  const off = summary.data === null;
  // With billing off the header keeps today's line: the status and the period.
  const plan = useQuery({ ...planQuery, enabled: off });
  const line = summary.data
    ? statusLine(summary.data)
    : off && plan.data
      ? periodLine(plan.data.status, plan.data.period_start, plan.data.period_end)
      : null;
  const tabs = TABS.filter((t) => !(t.needsBilling && off));
  const [actions, setActions] = useState<HTMLDivElement | null>(null);
  return (
    <>
      <div className="flex max-w-[880px] items-end justify-between gap-4 pb-5">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="-mt-1 mb-1 empty:hidden">
            <Crumbs />
          </div>
          <h1 className="font-display font-medium text-hero">Billing</h1>
          {line !== null ? (
            <p className="text-caption text-ink-3">{line}</p>
          ) : summary.isError ? null : (
            <Skeleton className="my-1 h-2.5 w-32" />
          )}
        </div>
        <div ref={setActions} className="flex shrink-0 items-center gap-2 empty:hidden" />
      </div>
      {/* The tabs scroll sideways on a phone; the page never does. */}
      <div className="max-w-[880px] overflow-x-auto">
        <Tabs
          label="Billing"
          value={tabOf(pathname)}
          tabs={tabs}
          onChange={(to) => navigate({ to, replace: false })}
        />
      </div>
      <BillingHeaderSlot value={actions}>
        <Outlet />
      </BillingHeaderSlot>
    </>
  );
}
